import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Alert, AlertTitle } from "@mui/material";

import styles from "./SignupPage.module.css";
import usePageTitle from "../../hooks/usePageTitle";
import { getEmailOTP, getPhoneOTP, verifyEmailOTP, verifyPhoneOTP } from "../../api/auth";
import GlobalIcon from "../../components/GlobalIcon/GlobalIcon";
import OTPInput from "../../components/OTPInput/OTPInput";

// Carousel slides data
const carouselSlides = [
    {
        image: "/SignupBanner.png",
        heading: "Start your Investment Journey Today",
        text: "Join Millions of investors on this platform. Create your account to manage your Family portfolio, Holdings and Execute trades seamlessly."
    },
    {
        image: "/SignupBanner.png",
        heading: "Smart Trading Made Simple",
        text: "Access real-time market data, advanced analytics, and automated trading strategies. Make informed decisions with our comprehensive platform."
    },
    {
        image: "/SignupBanner.png",
        heading: "Secure & Reliable Platform",
        text: "Your investments are protected with bank-level security. Experience seamless transactions and 24/7 customer support for all your trading needs."
    }
];

function SignupPage() {
    usePageTitle();
    const navigate = useNavigate();

    // Form state (Step 1)
    const [form, setForm] = useState({
        email: "",
        countryCode: "+91",
        phone: "",
        error: "",
        termsAccepted: false,
        fieldErrors: {
            email: false,
            phone: false,
            terms: false
        }
    });

    // Email OTP state
    const [emailOTPState, setEmailOTPState] = useState({
        showOTP: false,
        otp: "",
        isSending: false,
        isVerifying: false,
        verified: false,
        timer: 0,
        error: false
    });

    // Phone OTP state
    const [phoneOTPState, setPhoneOTPState] = useState({
        showOTP: false,
        otp: "",
        isSending: false,
        isVerifying: false,
        verified: false,
        timer: 0,
        error: false
    });

    // Step 2 state (Personal Information)
    const [step2, setStep2] = useState({
        show: false,
        firstName: "",
        lastName: "",
        password: "",
        showPassword: false,
        fieldErrors: {
            firstName: false,
            lastName: false,
            password: false,
            terms: false
        }
    });

    // Carousel state
    const [carousel, setCarousel] = useState({
        currentSlide: 0,
        isTransitioning: false
    });

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const validatePhone = (phone) => {
        const phoneRegex = /^\d{10}$/;
        return phoneRegex.test(phone);
    };

    // Countdown timer effect for email OTP
    useEffect(() => {
        if (emailOTPState.timer > 0) {
            const timer = setTimeout(() => {
                setEmailOTPState(prev => ({ ...prev, timer: prev.timer - 1 }));
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [emailOTPState.timer]);

    // Countdown timer effect for phone OTP
    useEffect(() => {
        if (phoneOTPState.timer > 0) {
            const timer = setTimeout(() => {
                setPhoneOTPState(prev => ({ ...prev, timer: prev.timer - 1 }));
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [phoneOTPState.timer]);

    // Handle Get Email OTP
    const handleGetEmailOTP = async (e) => {
        e.preventDefault();
        setForm(prev => ({ ...prev, error: "" }));

        if (!form.email || !validateEmail(form.email)) {
            return;
        }

        setEmailOTPState(prev => ({ ...prev, isSending: true }));
        try {
            await getEmailOTP(form.email);
            setEmailOTPState(prev => ({ ...prev, showOTP: true, timer: 240, isSending: false })); // 4 minutes timer
            setForm(prev => ({ ...prev, fieldErrors: { ...prev.fieldErrors, email: false }, error: "" }));
        } catch (err) {
            setForm(prev => ({ ...prev, error: err?.response?.data?.detail || err?.response?.data?.message || "Failed to send OTP. Please try again." }));
            setEmailOTPState(prev => ({ ...prev, isSending: false }));
        }
    };

    // Handle Get Phone OTP
    const handleGetPhoneOTP = async (e) => {
        e.preventDefault();
        setForm(prev => ({ ...prev, error: "" }));

        if (!form.phone || !validatePhone(form.phone)) {
            return;
        }

        setPhoneOTPState(prev => ({ ...prev, isSending: true }));
        try {
            const fullPhoneNumber = `${form.countryCode}${form.phone}`;
            await getPhoneOTP(fullPhoneNumber);
            setPhoneOTPState(prev => ({ ...prev, showOTP: true, timer: 240, isSending: false })); // 4 minutes timer
            setForm(prev => ({ ...prev, fieldErrors: { ...prev.fieldErrors, phone: false }, error: "" }));
        } catch (err) {
            setForm(prev => ({ ...prev, error: err?.response?.data?.detail || err?.response?.data?.message || "Failed to send OTP. Please try again." }));
            setPhoneOTPState(prev => ({ ...prev, isSending: false }));
        }
    };

    // Auto-verify email OTP when 6 digits are entered
    useEffect(() => {
        if (emailOTPState.otp.length === 6 && !emailOTPState.verified && !emailOTPState.isVerifying) {
            const verifyEmail = async () => {
                setEmailOTPState(prev => ({ ...prev, isVerifying: true, error: false }));
                setForm(prev => ({ ...prev, error: "" }));
                try {
                    await verifyEmailOTP(form.email, emailOTPState.otp);
                    setEmailOTPState(prev => ({ ...prev, verified: true, error: false, isVerifying: false }));
                    setForm(prev => ({ ...prev, error: "" }));
                } catch (err) {
                    setEmailOTPState(prev => ({ ...prev, error: true, otp: "", isVerifying: false }));
                }
            };
            verifyEmail();
        }
    }, [emailOTPState.otp, emailOTPState.verified, emailOTPState.isVerifying, form.email]);

    // Auto-verify phone OTP when 6 digits are entered
    useEffect(() => {
        if (phoneOTPState.otp.length === 6 && !phoneOTPState.verified && !phoneOTPState.isVerifying) {
            const verifyPhone = async () => {
                setPhoneOTPState(prev => ({ ...prev, isVerifying: true, error: false }));
                setForm(prev => ({ ...prev, error: "" }));
                try {
                    const fullPhoneNumber = `${form.countryCode}${form.phone}`;
                    await verifyPhoneOTP(fullPhoneNumber, phoneOTPState.otp);
                    setPhoneOTPState(prev => ({ ...prev, verified: true, error: false, isVerifying: false }));
                    setForm(prev => ({ ...prev, error: "" }));
                } catch (err) {
                    setPhoneOTPState(prev => ({ ...prev, error: true, otp: "", isVerifying: false }));
                }
            };
            verifyPhone();
        }
    }, [phoneOTPState.otp, phoneOTPState.verified, phoneOTPState.isVerifying, form.countryCode, form.phone]);

    // Resend Email OTP
    const handleResendEmailOTP = async () => {
        if (emailOTPState.timer > 0) return;
        setEmailOTPState(prev => ({ ...prev, error: false, otp: "" }));
        await handleGetEmailOTP({ preventDefault: () => {} });
    };

    // Resend Phone OTP
    const handleResendPhoneOTP = async () => {
        if (phoneOTPState.timer > 0) return;
        setPhoneOTPState(prev => ({ ...prev, error: false, otp: "" }));
        await handleGetPhoneOTP({ preventDefault: () => {} });
    };

    // Format timer as MM:SS
    const formatTimer = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    };

    // Handle final confirmation/submit (Step 1 - OTP Verification)
    const handleConfirm = async (e) => {
        e.preventDefault();
        setForm(prev => ({ ...prev, error: "" }));

        // Validate that both email and phone are verified
        if (!emailOTPState.verified || !phoneOTPState.verified) {
            setForm(prev => ({ ...prev, error: "Please verify both email and phone number with OTP" }));
            return;
        }

        if (!form.termsAccepted) {
            setForm(prev => ({ 
                ...prev, 
                error: "Please accept the Privacy Policy and Electronics Communication Policy to continue",
                fieldErrors: { ...prev.fieldErrors, terms: true }
            }));
            return;
        }

        // Move to step 2
        setStep2(prev => ({ ...prev, show: true }));
        setForm(prev => ({ ...prev, error: "" }));
    };

    // Handle Step 2 submission (Personal Information)
    const handleStep2Submit = async (e) => {
        e.preventDefault();
        setForm(prev => ({ ...prev, error: "" }));

        // Validate fields
        const errors = {
            firstName: !step2.firstName.trim(),
            lastName: !step2.lastName.trim(),
            password: !step2.password,
            terms: !form.termsAccepted
        };

        setStep2(prev => ({ ...prev, fieldErrors: errors }));

        // Check if there are any errors
        if (errors.firstName || errors.lastName || errors.password || errors.terms) {
            const errorMessage = errors.terms 
                ? "Please accept the Privacy Policy and Electronics Communication Policy to continue"
                : "Please fill in all fields";
            setForm(prev => ({ ...prev, error: errorMessage }));
            return;
        }

        // Password validation
        if (step2.password.length < 8) {
            setForm(prev => ({ ...prev, error: "Password must be at least 8 characters" }));
            setStep2(prev => ({ ...prev, fieldErrors: { ...prev.fieldErrors, password: true } }));
            return;
        }

        // Save contact details for later use in broker connection flow
        const fullPhoneNumber = `${form.countryCode}${form.phone}`;
        localStorage.setItem("user_email", form.email);
        localStorage.setItem("user_phone", fullPhoneNumber);
        localStorage.setItem("first_name", step2.firstName);
        localStorage.setItem("last_name", step2.lastName);
        
        // Navigate to broker selection after successful step 2 completion
        navigate("/select-broker", {
            state: {
                email: form.email,
                phone: fullPhoneNumber,
                firstName: step2.firstName,
                lastName: step2.lastName
            }
        });
    };

    // Handle carousel navigation
    const goToSlide = (index) => {
        if (index === carousel.currentSlide || carousel.isTransitioning) return;
        setCarousel(prev => ({ ...prev, isTransitioning: true, currentSlide: index }));
        setTimeout(() => {
            setCarousel(prev => ({ ...prev, isTransitioning: false }));
        }, 500); // Match CSS transition duration
    };

    // Auto-play carousel
    useEffect(() => {
        const interval = setInterval(() => {
            if (!carousel.isTransitioning) {
                setCarousel(prev => ({ ...prev, currentSlide: (prev.currentSlide + 1) % carouselSlides.length }));
            }
        }, 5000); // Change slide every 5 seconds

        return () => clearInterval(interval);
    }, [carousel.isTransitioning]);

    return (
        <div className={styles["container"]}>
            {/* Left Section */}
            <div className={styles["left-section-wrapper"]}>
                <div className={styles["left-section"]}>
                    {/* Background Pattern SVG */}
                    <div className={styles["bg-pattern"]}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="123" height="136" viewBox="0 0 123 136" fill="none">
                            <rect x="33" width="90" height="84" fill="#D9D9D9" fillOpacity="0.1" />
                            <rect x="1.5" y="53.5" width="87" height="81" stroke="white" strokeOpacity="0.1" strokeWidth="3" />
                        </svg>
                    </div>
                    <div className={styles["logo-container"]}>
                        <svg
                            width="33"
                            height="37"
                            viewBox="0 0 33 37"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className={styles["logo"]}
                        >
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M32.7149 25.9982V10.7913C32.7149 9.65245 32.1074 8.60009 31.1208 8.0305L17.9515 0.427194C16.9653 -0.142398 15.75 -0.142398 14.7637 0.427194L1.59376 8.0305C0.6075 8.60009 0 9.65245 0 10.7913V25.9982C0 27.1371 0.6075 28.1898 1.59376 28.7591L14.7637 36.3624C15.75 36.9319 16.9653 36.9319 17.9515 36.3624L31.1208 28.7591C32.1074 28.1898 32.7149 27.1371 32.7149 25.9982Z"
                                fill="white"
                            />
                            <path
                                d="M8.23406 18.344C8.23406 18.344 8.26339 20.5587 8.32206 24.988C8.32206 25.296 8.23406 25.5673 8.05806 25.802C7.89673 26.022 7.66206 26.132 7.35406 26.132C7.04606 26.132 6.79673 26.022 6.60606 25.802C6.41539 25.5673 6.32006 25.296 6.32006 24.988L6.43006 18.344L6.32006 11.854C6.32006 11.546 6.41539 11.2747 6.60606 11.04C6.79673 10.8053 7.04606 10.688 7.35406 10.688C7.66206 10.688 7.89673 10.8053 8.05806 11.04C8.23406 11.26 8.32206 11.568 8.32206 11.964C8.32206 12.36 8.30739 13.1373 8.27806 14.296C8.26339 15.44 8.25606 16.1587 8.25606 16.452C9.07739 15.0733 10.2361 13.768 11.7321 12.536C13.2427 11.304 14.4454 10.688 15.3401 10.688C15.6774 10.688 15.9561 10.7907 16.1761 10.996C16.4107 11.2013 16.5281 11.4653 16.5281 11.788C16.5281 12.1107 16.4107 12.3747 16.1761 12.58C15.9561 12.7853 15.7287 12.888 15.4941 12.888C15.2594 12.888 15.0174 12.8293 14.7681 12.712C14.5334 12.58 14.2694 12.514 13.9761 12.514C13.6827 12.514 13.2354 12.734 12.6341 13.174C12.0327 13.614 11.3874 14.2007 10.6981 14.934C10.0381 15.6673 9.50273 16.3567 9.09206 17.002C13.4774 22.6927 18.7794 26.9973 24.9981 29.916C25.9514 30.356 26.4281 30.8547 26.4281 31.412C26.4281 31.7053 26.3254 31.94 26.1201 32.116C25.9147 32.292 25.6287 32.38 25.2621 32.38C24.3527 32.38 22.8274 31.6173 20.6861 30.092C18.5594 28.5667 16.3521 26.7407 14.0641 24.614C13.0227 23.6607 11.9447 22.5827 10.8301 21.38C9.73006 20.1627 8.86473 19.1507 8.23406 18.344Z"
                                fill="#4F46E5"
                            />
                        </svg>
                        <span className={styles["logo-text"]}>KnI Wealth</span>
                    </div>

                    {/* Carousel Container */}
                    <div className={styles["carousel-container"]}>
                        <div
                            className={styles["carousel-track"]}
                            style={{
                                transform: `translateX(-${carousel.currentSlide * 100}%)`,
                                transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
                            }}
                        >
                            {carouselSlides.map((slide, index) => (
                                <div key={index} className={styles["carousel-slide"]}>

                                    <div className={styles["pnl-card"]}>
                                        <img
                                            src={slide.image}
                                            alt="Profit preview"
                                            className={styles["pnl-image"]}

                                        />
                                    </div>
                                    <div className={styles["promotional-content"]}>
                                        <h1 className={styles["promo-heading"]}>
                                            {slide.heading}
                                        </h1>
                                        <p className={styles["promo-text"]}>
                                            {slide.text}
                                        </p>
                                    </div>

                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Carousel Indicators */}
                    <div className={styles["carousel-indicators"]}>
                        {carouselSlides.map((_, index) => (
                            <button
                                key={index}
                                type="button"
                                className={`${styles["indicator"]} ${index === carousel.currentSlide ? styles["indicator-active"] : ""}`}
                                onClick={() => goToSlide(index)}
                                aria-label={`Go to slide ${index + 1}`}
                            />
                        ))}
                    </div>
                </div>
            </div>
            {/* Right Section */}
            <div className={styles["right-section"]}>
                <form className={styles["form-container"]} onSubmit={step2.show ? handleStep2Submit : handleConfirm}>
                    <h2 className={styles["form-heading"]}>Sign up for an account</h2>
                    <p className={styles["form-subtitle"]}>Create your account to get the access</p>

                    {/* Important Requirement Box */}
                    <Alert
                        variant="outlined"
                        severity="info"
                        sx={{
                            marginBottom: '24px',
                            backgroundColor: '#F1F6FD',
                            borderColor: '#A7C5FD',
                            borderRadius: '3.528px',
                            '& .MuiAlert-icon': {
                                color: '#0052EA',
                                '& svg': {
                                    color: '#0052EA'
                                }
                            },
                            '& .MuiAlertTitle-root': {
                                color: 'var(--Dark-Gray, #181818)',
                                fontFamily: 'Inter',
                                fontSize: '16px',
                                fontStyle: 'normal',
                                fontWeight: 600,
                                lineHeight: '21.17px',
                                marginBottom: '12px'
                            },
                            '& .MuiAlert-message': {
                                color: 'var(--Dark-Gray, #181818)',
                                fontFamily: 'Inter',
                                fontSize: '14px',
                                fontStyle: 'normal',
                                fontWeight: 400,
                                lineHeight: '17.642px'
                            }
                        }}
                    >
                        <AlertTitle>Important Requirement</AlertTitle>
                        You must ensure that the same mobile number or email address is mapped to your Demate account
                    </Alert>

                    <div className={styles["form-content"]}>
                                {form.error !== "" && (
                                    <div className={styles["error-message"]}>{form.error}</div>
                                )}

                        {!step2.show ? (
                            <>
                                {/* Email Input */}
                                <div className={styles["input-group"]}>
                                    <div className={styles["input-wrapper"]}>
                                        <span className={styles["input-icon"]}>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="19" height="15" viewBox="0 0 19 15" fill="none">
                                                <path d="M17.5 2.50001C17.5 1.58334 16.75 0.833344 15.8334 0.833344H2.50004C1.58337 0.833344 0.833374 1.58334 0.833374 2.50001M17.5 2.50001V12.5C17.5 13.4167 16.75 14.1667 15.8334 14.1667H2.50004C1.58337 14.1667 0.833374 13.4167 0.833374 12.5V2.50001M17.5 2.50001L9.16671 8.33334L0.833374 2.50001" stroke="#667085" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </span>
                                        <input
                                            type="email"
                                    className={`${styles["input"]} ${styles["email-input"]} ${form.fieldErrors.email ? styles["input-error"] : ""} ${emailOTPState.verified ? styles["input-verified"] : ""}`}
                                            placeholder="1234@gmail.com"
                                            value={form.email}
                                            onChange={(e) => {
                                                setForm(prev => ({ ...prev, email: e.target.value, fieldErrors: { ...prev.fieldErrors, email: false } }));
                                                // Reset verification if email changes
                                                if (emailOTPState.verified) {
                                                    setEmailOTPState(prev => ({ ...prev, verified: false, showOTP: false, otp: "" }));
                                                }
                                    }}
                                    disabled={emailOTPState.verified || emailOTPState.isVerifying}
                                />
                                {emailOTPState.verified ? (
                                    <div className={styles["verified-icon"]}>
                                        <GlobalIcon iconName="VerifiedIcon" className={styles["checkmark-icon"]} />
                                    </div>
                                ) : (
                                    validateEmail(form.email) && (
                                        <button
                                            type="button"
                                            className={styles["get-otp-button"]}
                                            onClick={handleGetEmailOTP}
                                            disabled={emailOTPState.isSending || emailOTPState.verified}
                                        >
                                            {emailOTPState.isSending ? "Sending..." : "Get OTP"}
                                        </button>
                                    )
                                )}
                            </div>
                            
                            {/* Email OTP Input */}
                            {emailOTPState.showOTP && !emailOTPState.verified && (
                                <OTPInput
                                    otp={emailOTPState.otp}
                                    setOtp={(otp) => setEmailOTPState(prev => ({ ...prev, otp }))}
                                    isVerifying={emailOTPState.isVerifying}
                                    isVerified={emailOTPState.verified}
                                    timer={emailOTPState.timer}
                                    formatTimer={formatTimer}
                                    onResend={handleResendEmailOTP}
                                    hasError={emailOTPState.error}
                                    onErrorClear={() => setEmailOTPState(prev => ({ ...prev, error: false }))}
                                />
                            )}
                                </div>

                                {/* Phone Input */}
                                <div className={styles["input-group"]}>
                                    <div className={styles["phone-input-wrapper"]}>
                                        <input
                                            type="tel"
                                            className={`${styles["input"]} ${styles["country-code-input"]}`}
                                            placeholder="+91"
                                            value={form.countryCode}
                                    onChange={(e) => {
                                        setForm(prev => ({ ...prev, countryCode: e.target.value }));
                                        // Reset verification if country code changes
                                        if (phoneOTPState.verified) {
                                            setPhoneOTPState(prev => ({ ...prev, verified: false, showOTP: false, otp: "" }));
                                        }
                                    }}
                                    disabled={phoneOTPState.verified}
                                />
                                <div className={styles["phone-input-with-button"]}>
                                        <input
                                            type="tel"
                                        className={`${styles["input"]} ${styles["phone-number-input"]} ${form.fieldErrors.phone ? styles["input-error"] : ""} ${phoneOTPState.verified ? styles["input-verified"] : ""}`}
                                            placeholder="9876543210"
                                            value={form.phone}
                                            onChange={(e) => {
                                            const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                                            setForm(prev => ({ ...prev, phone: value, fieldErrors: { ...prev.fieldErrors, phone: false } }));
                                            // Reset verification if phone changes
                                            if (phoneOTPState.verified) {
                                                setPhoneOTPState(prev => ({ ...prev, verified: false, showOTP: false, otp: "" }));
                                            }
                                        }}
                                        disabled={phoneOTPState.verified || phoneOTPState.isVerifying}
                                    />
                                    {phoneOTPState.verified ? (
                                        <div className={styles["verified-icon"]}>
                                            <GlobalIcon iconName="VerifiedIcon" className={styles["checkmark-icon"]} />
                                        </div>
                                    ) : (
                                        validatePhone(form.phone) && (
                                            <button
                                                type="button"
                                                className={styles["get-otp-button"]}
                                                onClick={handleGetPhoneOTP}
                                                disabled={phoneOTPState.isSending || phoneOTPState.verified}
                                            >
                                                {phoneOTPState.isSending ? "Sending..." : "Get OTP"}
                                            </button>
                                        )
                                    )}
                                    </div>
                                    </div>
                            
                            {/* Phone OTP Input */}
                            {phoneOTPState.showOTP && !phoneOTPState.verified && (
                                <OTPInput
                                    otp={phoneOTPState.otp}
                                    setOtp={(otp) => setPhoneOTPState(prev => ({ ...prev, otp }))}
                                    isVerifying={phoneOTPState.isVerifying}
                                    isVerified={phoneOTPState.verified}
                                    timer={phoneOTPState.timer}
                                    formatTimer={formatTimer}
                                    onResend={handleResendPhoneOTP}
                                    hasError={phoneOTPState.error}
                                    onErrorClear={() => setPhoneOTPState(prev => ({ ...prev, error: false }))}
                                />
                            )}
                        </div>

                        {/* Terms and Conditions */}
                                <div className={styles["terms-container"]}>
                                    <label className={styles["terms-checkbox-label"]}>
                                        <input
                                            type="checkbox"
                                            checked={form.termsAccepted}
                                            onChange={(e) => {
                                                setForm(prev => ({ ...prev, termsAccepted: e.target.checked, fieldErrors: { ...prev.fieldErrors, terms: false } }));
                                            }}
                                            className={styles["terms-checkbox"]}
                                            aria-label="Accept terms and conditions"
                                        />
                                        <span className={styles["terms-text"]}>
                                            By creating an account, you agreeing to our{" "}
                                            <Link to="/privacy-policy" className={styles["primary-link"]}>
                                                Privacy Policy
                                            </Link>
                                            , and{" "}
                                            <Link to="/electronics-communication-policy" className={styles["primary-link"]}>
                                                Electronics Communication Policy
                                            </Link>
                                            .
                                        </span>
                                    </label>
                                </div>

                                {/* Confirm Button */}
                                <button
                                    type="submit"
                                    className={styles["submit-button"]}
                                    disabled={!emailOTPState.verified || !phoneOTPState.verified || !form.termsAccepted}
                                >
                                    Confirm
                                </button>

                                {/* Separator */}
                                <div className={styles["divider"]}>
                                    <span className={styles["divider-text"]}>or</span>
                                </div>

                                {/* Login Link */}
                                <div className={styles["login-link-container"]}>
                                    <span className={styles["login-text"]}>Already have an account? </span>
                                    <Link to="/login" className={styles["primary-link"]}>Sign in</Link>
                                </div>
                            </>
                        ) : (
                            <>
                                {/* Step 2: Personal Information */}
                                {/* Name Inputs */}
                                <div className={styles["name-inputs-wrapper"]}>
                                    <div className={styles["input-group"]} style={{ flex: 1, marginRight: '8px', marginBottom: 0 }}>
                                        <input
                                            type="text"
                                            className={`${styles["input"]} ${step2.fieldErrors.firstName ? styles["input-error"] : ""}`}
                                            placeholder="First Name"
                                            value={step2.firstName}
                                            onChange={(e) => {
                                                setStep2(prev => ({ ...prev, firstName: e.target.value, fieldErrors: { ...prev.fieldErrors, firstName: false } }));
                                            }}
                                        />
                                    </div>
                                    <div className={styles["input-group"]} style={{ flex: 1, marginLeft: '8px', marginBottom: 0 }}>
                                        <input
                                            type="text"
                                            className={`${styles["input"]} ${step2.fieldErrors.lastName ? styles["input-error"] : ""}`}
                                            placeholder="Last Name"
                                            value={step2.lastName}
                                            onChange={(e) => {
                                                setStep2(prev => ({ ...prev, lastName: e.target.value, fieldErrors: { ...prev.fieldErrors, lastName: false } }));
                                            }}
                                        />
                                    </div>
                                </div>

                                {/* Password Input */}
                                <div className={styles["input-group"]}>
                                    <div className={styles["input-wrapper"]}>
                                        <input
                                            type={step2.showPassword ? "text" : "password"}
                                            className={`${styles["input"]} ${step2.fieldErrors.password ? styles["input-error"] : ""}`}
                                            placeholder="Create Password"
                                            value={step2.password}
                                            onChange={(e) => {
                                                setStep2(prev => ({ ...prev, password: e.target.value, fieldErrors: { ...prev.fieldErrors, password: false } }));
                                            }}
                                        />
                                        <button
                                            type="button"
                                            className={styles["password-toggle"]}
                                            onClick={() => setStep2(prev => ({ ...prev, showPassword: !prev.showPassword }))}
                                            aria-label={step2.showPassword ? "Hide password" : "Show password"}
                                        >
                                                    {step2.showPassword ? (
                                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                    <path d="M2.5 3.75L17.5 16.25M8.15625 8.90625C7.84375 9.21875 7.65625 9.65625 7.65625 10.15625C7.65625 11.1875 8.46875 12 9.5 12C10 12 10.4375 11.8125 10.75 11.5M15.40625 13.34375C14.1875 14.25 12.65625 14.84375 11 14.84375C6.4375 14.84375 2.8125 11.21875 1.25 10C1.9375 8.78125 2.875 7.71875 3.96875 6.84375M8.75 5.03125C9.34375 4.9375 9.96875 4.875 10.625 4.875C15.1875 4.875 18.8125 8.5 20.375 9.71875C19.875 10.5 19.1875 11.21875 18.375 11.8125" stroke="#667085" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                            ) : (
                                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                    <path d="M10 4.375C14.5625 4.375 18.1875 8 19.75 9.21875C19.5 9.75 19.0625 10.2812 18.4375 10.7812M5.5625 5.5625C3.3125 7.0625 1.75 9.125 1.25 10C2.8125 11.2188 6.4375 14.8438 11 14.8438C12.4375 14.8438 13.75 14.5 14.9375 13.9375M2.5 2.5L17.5 17.5M7.34375 7.34375C6.625 8.0625 6.15625 9.03125 6.15625 10.15625C6.15625 12.21875 7.78125 13.84375 9.84375 13.84375C10.96875 13.84375 11.9375 13.375 12.65625 12.65625" stroke="#667085" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                            )}
                                        </button>
                                    </div>
                                    <div className={styles["password-hint"]}>
                                        Use 8+ characters with a mix of letters and numbers
                                    </div>
                                </div>

                                {/* Terms and Conditions */}
                                <div className={styles["terms-container"]}>
                                    <label className={styles["terms-checkbox-label"]}>
                                        <input
                                            type="checkbox"
                                            checked={form.termsAccepted}
                                            onChange={(e) => {
                                                setForm(prev => ({ ...prev, termsAccepted: e.target.checked }));
                                                setStep2(prev => ({ ...prev, fieldErrors: { ...prev.fieldErrors, terms: false } }));
                                            }}
                                            className={styles["terms-checkbox"]}
                                            aria-label="Accept terms and conditions"
                                        />
                                        <span className={styles["terms-text"]}>
                                            By creating an account, you agreeing to our{" "}
                                            <Link to="/privacy-policy" className={styles["primary-link"]}>
                                                Privacy Policy
                                            </Link>
                                            , and{" "}
                                            <Link to="/electronics-communication-policy" className={styles["primary-link"]}>
                                                Electronics Communication Policy
                                            </Link>
                                            .
                                        </span>
                                    </label>
                                </div>

                                {/* Confirm Button */}
                                <button
                                    type="submit"
                                    className={styles["submit-button"]}
                                    disabled={!step2.firstName || !step2.lastName || !step2.password || step2.password.length < 8 || !form.termsAccepted}
                                >
                                    Confirm
                                </button>

                                {/* Separator */}
                                <div className={styles["divider"]}>
                                    <span className={styles["divider-text"]}>or</span>
                                </div>

                                {/* Login Link */}
                                <div className={styles["login-link-container"]}>
                                    <span className={styles["login-text"]}>Already have an account? </span>
                                    <Link to="/login" className={styles["primary-link"]}>Sign in</Link>
                                </div>
                            </>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
}

export default SignupPage;

